"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CharacteristicExpressionParser = void 0;
const mathjs = require("mathjs");
const CommonParameterUnits_1 = require("../CommonParameterUnits");
const DesignModels_1 = require("../DesignModels");
const ComponentValidationResult_1 = require("../ComponentValidationResult");
const Errors_1 = require("../Errors");
const __1 = require("..");
class CharacteristicExpressionParser {
    static validateComponentExpression(componentData, expression, characteristic) {
        try {
            const executableExpression = this.parseComponentExpression(componentData, expression, characteristic);
            const context = Object.fromEntries(executableExpression.variables.map((v) => [v.id, 1]));
            const result = executableExpression.evaluate(context);
            if (Number.isNaN(result) || !Number.isFinite(result)) {
                return [
                    this.transformError(new Error(`Expression evaluates to NaN or Infinity: ${expression}`), expression, characteristic),
                ];
            }
            return [];
        }
        catch (err) {
            return [this.transformError(err, expression, characteristic)];
        }
    }
    static parseComponentExpression(componentOrSignals, expression, characteristic, fixFloatingPointErrors = false, throwOnMissingSignals = true) {
        const signals = Array.isArray(componentOrSignals) ? componentOrSignals : componentOrSignals.signals;
        const units = CommonParameterUnits_1.CommonParameterUnits[characteristic];
        if (!units) {
            throw new Error(`Unsupported characteristic: ${characteristic}`);
        }
        if (expression.trim() === '') {
            throw new Error('Empty expression');
        }
        const parsedSignalsExpression = this.extractSignalVariables(expression, units, signals);
        expression = parsedSignalsExpression.expression;
        const parsed = this.parseExpression(expression, characteristic, parsedSignalsExpression.variables, fixFloatingPointErrors);
        if (throwOnMissingSignals) {
            const missingVariables = parsed.variables.filter((v) => !v.signal);
            if (missingVariables.length) {
                throw new Error(`Missing related signals for variables in expression: ${missingVariables.map((v) => v.id).join(', ')}`);
            }
        }
        return parsed;
    }
    static parseExpression(expression, characteristic, parsedVariables = [], fixFloatingPointErrors = false) {
        const units = CommonParameterUnits_1.CommonParameterUnits[characteristic];
        if (!units) {
            throw new Error(`Unsupported characteristic: ${characteristic}`);
        }
        const expressionVariables = [...parsedVariables];
        const normalizedExpr = this.normalizeExpression(expression, units, fixFloatingPointErrors);
        const variables = this.extractVariables(normalizedExpr);
        expressionVariables.push(...variables
            .filter((v) => !parsedVariables?.find((ev) => ev.id === v))
            .map((v) => ({ id: v, originalName: v, signal: undefined })));
        return {
            expression: normalizedExpr,
            variables: expressionVariables,
            characteristic,
            evaluate: (context) => {
                return mathjs.evaluate(normalizedExpr, context);
            },
        };
    }
    static tryEvaluateConstant(expression) {
        if (expression.variables.length > 0) {
            return undefined;
        }
        try {
            const result = expression.evaluate({});
            return typeof result === 'number' && Number.isFinite(result) ? result : undefined;
        }
        catch {
            return undefined;
        }
    }
    static evaluateExpressionWithAbsoluteCharacteristicValues(expression, context) {
        const errors = [];
        const safeEvaluate = (valueExtractor, fieldName) => {
            try {
                const evalContext = Object.fromEntries(Object.entries(context).map(([key, value]) => [key, valueExtractor(value)]));
                const undefinedKeys = Object.entries(evalContext)
                    .filter(([_, val]) => __1.Utils.isEmptyValue(val))
                    .map(([key]) => key);
                if (undefinedKeys.length > 0) {
                    return undefined;
                }
                return expression.evaluate(evalContext);
            }
            catch (error) {
                const wrappedError = new Errors_1.ErrorWithInternal(`Failed to evaluate ${fieldName} for expression ${expression.expression}`, error instanceof Error ? error : new Error(`Error: ${String(error)}`));
                errors.push(wrappedError);
                return undefined;
            }
        };
        return {
            value: {
                value: safeEvaluate((value) => value.value, 'nominal value'),
                allowableRange: {
                    min: safeEvaluate((value) => value.allowableRange.min, 'minimum value'),
                    max: safeEvaluate((value) => value.allowableRange.max, 'maximum value'),
                },
                discreteValues: [],
            },
            errors: errors.length ? errors : undefined,
        };
    }
    static extractVariables(expression) {
        try {
            const node = mathjs.parse(expression);
            const symbols = new Set();
            node.traverse((node) => {
                if (node.type === 'SymbolNode') {
                    symbols.add(node.name);
                }
            });
            return Array.from(symbols);
        }
        catch (error) {
            throw new Errors_1.ErrorWithInternal(`Error parsing expression: "${expression}"`, error);
        }
    }
    static normalizeExpression(expression, units, fixFloatingPointErrors = false) {
        expression = expression.replace(/ x /g, ' * ');
        expression = __1.Utils.convertStringExpressionToAscii(expression);
        expression = this.convertPowerNotationToScientific(expression);
        const operands = __1.Utils.getOperandsFromExpression(expression);
        for (const operand of operands) {
            try {
                const value = __1.Utils.parseNumericalSIValue(operand, units);
                if (value !== undefined) {
                    let valueStr = value.toString();
                    if (fixFloatingPointErrors) {
                        valueStr = __1.Utils.correctFloatingPointErrors(value).toString();
                    }
                    expression = expression.replaceAll(operand, valueStr);
                }
            }
            catch {
            }
        }
        return expression;
    }
    static convertPowerNotationToScientific(expression) {
        const powerNotationRegex = /(\d+(?:\.\d+)?)\s*\*\s*10\s*\^\s*(-?\d+)/g;
        return expression.replace(powerNotationRegex, (_match, coefficient, exponent) => {
            return `${coefficient}e${exponent}`;
        });
    }
    static extractSignalVariables(expression, units, signals) {
        const expressionVariables = [];
        const hasCurlyBracedVariables = /\{\{[^}]+\}\}/.test(expression);
        if (hasCurlyBracedVariables) {
            const sigidPattern = /\{\{sigid_([^}]+)\}\}/g;
            let match;
            while ((match = sigidPattern.exec(expression)) !== null) {
                const signalId = match[1];
                const templatePattern = match[0];
                const signal = signals.find((s) => s.id.toString() === signalId);
                if (signal) {
                    let encodedSignalId = signal.id.toString();
                    if (!/^[a-zA-Z0-9_]+$/.test(encodedSignalId)) {
                        encodedSignalId = encodedSignalId.replace(/[^a-zA-Z0-9]/g, (match) => '_' + match.charCodeAt(0).toString(36));
                    }
                    const newVarName = `sigvar_${encodedSignalId}`;
                    expression = expression.replace(templatePattern, newVarName);
                    if (!expressionVariables.find((v) => v.id === newVarName)) {
                        expressionVariables.push({ id: newVarName, originalName: signal.name, signal });
                    }
                }
                else {
                    const placeholderVarName = `UNKNOWN_SIGID_${signalId}`;
                    expression = expression.replace(templatePattern, placeholderVarName);
                    expressionVariables.push({ id: placeholderVarName, originalName: templatePattern, signal: undefined });
                }
            }
        }
        else {
            const sortedSignals = Array.from(signals).sort((a, b) => {
                const aIncludesB = a.name.includes(b.name);
                const bIncludesA = b.name.includes(a.name);
                if (aIncludesB && !bIncludesA) {
                    return 1;
                }
                else if (!aIncludesB && bIncludesA) {
                    return -1;
                }
                else {
                    return a.name.length - b.name.length;
                }
            });
            for (const signal of sortedSignals) {
                if (typeof expression.replaceAll !== 'function') {
                    throw new Error(`Expression ${expression} is not a string`);
                }
                if (typeof signal.id !== 'string' ||
                    /^[^a-zA-Z]$/.test(signal.name) ||
                    !/^[a-zA-Z]/.test(signal.name) ||
                    signal.name === units.short ||
                    signal.name === units.long) {
                    continue;
                }
                expression = expression.replaceAll(new RegExp(`(^|\\W|\\d)(${signal.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})($|\\W|\\d)`, 'ig'), (_substr, prefix, originalName, suffix) => {
                    let encodedSignalId = signal.id.toString();
                    if (!/^[a-zA-Z0-9_]+$/.test(encodedSignalId)) {
                        encodedSignalId = encodedSignalId.replace(/[^a-zA-Z0-9]/g, (match) => '_' + match.charCodeAt(0).toString(36));
                    }
                    const newVarName = `sigvar_${encodedSignalId}`;
                    if (!expressionVariables.find((v) => v.id === newVarName)) {
                        expressionVariables.push({ id: newVarName, originalName, signal });
                    }
                    return `${prefix}${newVarName}${suffix}`;
                });
            }
        }
        return { expression, variables: expressionVariables };
    }
    static transformError(error, expression, characteristic) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error during expression validation';
        const componentModelError = new Errors_1.ComponentModelValidationError(ComponentValidationResult_1.ComponentValidationErrorType.ExpressionValidationError, errorMessage, DesignModels_1.BoundaryLevel.Error, undefined, error instanceof Error ? error : undefined);
        return {
            type: ComponentValidationResult_1.ComponentValidationErrorType.ExpressionValidationError,
            message: componentModelError.message,
            severity: componentModelError.severity,
            expression,
            characteristic: characteristic.toString(),
        };
    }
}
exports.CharacteristicExpressionParser = CharacteristicExpressionParser;
//# sourceMappingURL=CharacteristicExpressionParser.js.map